import { useMemo } from 'react';
import { useAdminAuthStore, type AdminRole, type AdminCompanyScope, type AdminUser } from '../stores/adminAuthStore';

const scopeHierarchy: AdminCompanyScope[] = ['assigned', 'company', 'all'];

interface AdminCapabilities {
  canManageSubscriptions: boolean;
  canManageCompanies: boolean;
  canManageAnnouncements: boolean;
  canViewKpiDashboard: boolean;
  canManageBilling: boolean;
  hasScope: (target: AdminCompanyScope) => boolean;
  requireRole: (roles: AdminRole[]) => boolean;
}

function deriveCapabilities(admin: AdminUser | null): AdminCapabilities {
  if (!admin) {
    return {
      canManageSubscriptions: false,
      canManageCompanies: false,
      canManageAnnouncements: false,
      canViewKpiDashboard: false,
      canManageBilling: false,
      hasScope: () => false,
      requireRole: () => false,
    };
  }

  const scopeIndex = scopeHierarchy.indexOf(admin.companyScope);

  const hasScope = (target: AdminCompanyScope) => {
    const targetIndex = scopeHierarchy.indexOf(target);
    if (scopeIndex === -1 || targetIndex === -1) {
      return false;
    }
    return scopeIndex >= targetIndex;
  };

  const requireRole = (roles: AdminRole[]) => roles.includes(admin.role);

  switch (admin.role) {
    case 'system_admin':
      return {
        canManageSubscriptions: true,
        canManageCompanies: true,
        canManageAnnouncements: true,
        canViewKpiDashboard: true,
        canManageBilling: true,
        hasScope,
        requireRole,
      };
    case 'company_admin':
      return {
        canManageSubscriptions: hasScope('company'),
        canManageCompanies: hasScope('company'),
        canManageAnnouncements: hasScope('company'),
        canViewKpiDashboard: true,
        canManageBilling: hasScope('company'),
        hasScope,
        requireRole,
      };
    case 'customer_success':
      return {
        canManageSubscriptions: hasScope('assigned'),
        canManageCompanies: hasScope('assigned'),
        canManageAnnouncements: hasScope('assigned'),
        canViewKpiDashboard: true,
        canManageBilling: false,
        hasScope,
        requireRole,
      };
    case 'finance':
      return {
        canManageSubscriptions: true,
        canManageCompanies: false,
        canManageAnnouncements: false,
        canViewKpiDashboard: true,
        canManageBilling: true,
        hasScope,
        requireRole,
      };
    default:
      return {
        canManageSubscriptions: false,
        canManageCompanies: false,
        canManageAnnouncements: false,
        canViewKpiDashboard: false,
        canManageBilling: false,
        hasScope,
        requireRole,
      };
  }
}

export function useAdminAccess() {
  const admin = useAdminAuthStore((state) => state.admin);
  const isAuthenticated = useAdminAuthStore((state) => state.isAuthenticated);
  const token = useAdminAuthStore((state) => state.token);

  const capabilities = useMemo(() => deriveCapabilities(admin), [admin]);

  return {
    admin,
    isAuthenticated,
    token,
    role: admin?.role ?? null,
    companyScope: admin?.companyScope ?? null,
    isSystemAdmin: admin?.role === 'system_admin',
    isCompanyAdmin: admin?.role === 'company_admin',
    isCustomerSuccess: admin?.role === 'customer_success',
    isFinance: admin?.role === 'finance',
    ...capabilities,
  };
}
